import { ButtonHTMLAttributes, forwardRef } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { cn } from '@/utils/cn'

type Variant = 'primary' | 'secondary' | 'ghost'
type Size = 'md' | 'lg'

const variants: Record<Variant, string> = {
  primary: 'eh-btn',
  secondary: 'eh-btn-secondary',
  ghost:
    'inline-flex items-center justify-center gap-2 rounded-[10px] px-3.5 py-2 text-eh-forest-ink hover:bg-eh-keylime transition-colors',
}

const sizes: Record<Size, string> = {
  md: '',
  lg: 'min-h-[56px] px-7 text-base',
}

interface BaseProps {
  variant?: Variant
  size?: Size
  className?: string
  showArrow?: boolean
  children: React.ReactNode
}

type ButtonAsButton = BaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { to?: undefined; href?: undefined }

type ButtonAsLink = BaseProps &
  Omit<LinkProps, 'className' | 'children'> & { href?: undefined }

type ButtonAsAnchor = BaseProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { to?: undefined; href: string }

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  props,
  ref,
) {
  const {
    variant = 'primary',
    size = 'md',
    className,
    showArrow,
    children,
    ...rest
  } = props as ButtonProps & { to?: string; href?: string }

  const classes = cn(variants[variant], sizes[size], className)
  const content = (
    <>
      {children}
      {showArrow ? <span className="eh-arrow" aria-hidden>→</span> : null}
    </>
  )

  if ('to' in props && props.to) {
    const { to, ...linkRest } = rest as Omit<LinkProps, 'className' | 'children'>
    return (
      <Link to={to} className={classes} {...linkRest}>
        {content}
      </Link>
    )
  }

  if ('href' in props && props.href) {
    const { href, ...anchorRest } = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>
    return (
      <a href={href} className={classes} {...anchorRest}>
        {content}
      </a>
    )
  }

  return (
    <button
      ref={ref}
      type={(rest as ButtonHTMLAttributes<HTMLButtonElement>).type || 'button'}
      className={classes}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  )
})

export default Button
